import { Component, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { InfoRacesComponent } from '../../components/info-races/info-races.component';
import { InfoGamesComponent } from '../../components/info-games/info-games.component';
import { HorseListComponent } from '../../components/horse-list/horse-list.component';
import { MiseComponent } from '../../components/mise/mise.component';
import { BetslipComponent } from '../../components/betslip/betslip.component';
import { PmuService } from '../../services/data/pmu.service';
import { Pari } from '../../models/pari.model';
import { Meeting } from '../../models/meeting.model';
import { Game, GameFormula } from '../../models/game.model';
import { combineLatest, distinctUntilChanged, Subscription, switchMap} from 'rxjs';
import { BetPayOptions } from '../../models/save-bet-options.model';
import { CE, CP, CT, SI } from '../../functions/horse-combination.function';
import { HeaderComponent } from '../../components/header/header.component';
import { LocaleService } from '../../services/locale/locale.service';
@Component({
  selector: 'app-detail-games',
  imports: [
    InfoRacesComponent,
    InfoGamesComponent,
    HorseListComponent,
    MiseComponent,
    BetslipComponent,
    HeaderComponent
  ],
  templateUrl: './detail-games.component.html',
  styleUrl: './detail-games.component.scss',
})
export class DetailGamesComponent implements OnInit, OnDestroy {
  @ViewChild(HorseListComponent) horseListComponent!: HorseListComponent;
  @ViewChild(BetslipComponent) betslipComponent!: BetslipComponent;

  meetings = signal<Meeting[]>([]);
  pari = signal<Partial<Pari>>({});
  raceGames = signal<Game[]>([]);
  betPayOptions = signal<Partial<BetPayOptions>>({});
  gameSubscription = new Subscription();
  selectedFormula: GameFormula = 'SI';
  selectedGame = signal<Partial<Game>>({});
  showAssociateSelect = signal<boolean>(false);
  showListDetails = signal<boolean>(false);
  isSpot: boolean = false;
  spotEnabled: boolean = false;
  completeFormulaEnabled: boolean = false;
  availableLocales = ['en-US', 'fr-FR'];
  selectedLocale =  localStorage.getItem('lang') ?? 'en-US';
  // isHorseSelected: boolean = false;
  subscriptions: Subscription = new Subscription();
  constructor(public readonly pmuService: PmuService, private readonly localeService: LocaleService) {
    this.pari.set(this.pmuService.getCurrentData());
    this.betPayOptions.set(pmuService.getBetOptionsData());
    this.localeService.setLanguages(this.availableLocales);
    this.selectedLocale = localStorage.getItem('lang') ?? 'en-US';
    this.localeService.setLocale(this.selectedLocale);
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  // state to check if any horse is selected
  get isHorseSelected() {
    // return (this.pari().baseSelections || []).length > 0 || (this.pari().associateSelections || []).length > 0;
    const index = this.selectedFormula && ['CP', 'CE', 'CT'].includes(this.selectedFormula) ? 1 : 0;
    return (this.pari().nCombine || 0) > index;
  }

  get selectedLang(){
    return this.selectedLocale.split('-')[0];
  }

  switchLanguage(lang: string) {
    this.localeService.setLocale(lang);
    this.selectedLocale = lang;
    localStorage.setItem('lang', lang);
  }

  get pariGame() {
    return this.pari().game ?? {} as Game
  }

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest(this.pmuService.getACData(), this.pmuService.paymentProviders$).pipe(distinctUntilChanged()).subscribe(([{countries, operators}, providersValue]) => {
        if (countries?.length) {
            this.pmuService.updateBetOptions({countries: countries});
          }
          if (operators?.length) {
            this.pmuService.updateBetOptions({operators: operators});
          }
          if(providersValue["CASHIN"]?.length){
            this.pmuService.updateBetOptions({providers: providersValue['CASHIN']})
          }
          this.betPayOptions.set(this.pmuService.getBetOptionsData())
        })
    );

    let meetingId = this.pari().meeting?.id;
    let raceId = this.pari().race?.id;
    let gameCode = this.pari().game?.code

    if(meetingId && raceId && gameCode){
      this.subscriptions.add(this.pmuService.getMeetings().subscribe((meetings) => {
        this.meetings.set(meetings);
        if (!this.pari().meeting?.id && !this.pari().race?.id) {
          let gameSubscription = this.pmuService.getGames(meetingId, raceId).subscribe((games) => {
            this.updateGameList(games, gameCode);
            gameSubscription.unsubscribe();
          });
        }
      }));
    }else {
      this.subscriptions.add(this.pmuService.getMeetings().subscribe((meetings) => {
        this.meetings.set(meetings);
        if (!this.pari().meeting?.id && !this.pari().race?.id) {
          this.pmuService.updateData({ meeting: this.meetings()[0] });
          this.pmuService.updateData({ race: this.meetings()[0].races[0] });
        }
      }));
    }

    this.subscriptions.add(
        this.pmuService.data$.pipe(
          distinctUntilChanged((p, c) => {
            this.pari.set(c);
            return (p.meeting.id === c.meeting.id) && (p.race.id === c.race.id);
          }),
          switchMap(pari => {
            if(pari.meeting?.id && pari.race?.id){
              return this.pmuService.getGames(pari.meeting.id, pari.race.id);
            }else {
              return [];
            }
          }),
        )
        .subscribe((games) => {
          this.updateGameList(games, this.pari().game?.code);
        })
      );
  }

  updateGameList(games: Game[], currentGameCode: string | undefined){
    this.raceGames.set(games);
    if(games.length){
      let game = games.find(g => g.code === currentGameCode) || games[0];
      if (game) {
        this.updateGame({game, formula: 'SI', spot: false, completeFormula: false, multi: 0});
        this.updateGameAmount({mise: game.baseStake, coefficient: 1});
      }
    }
  }

  updateSelected(selection: { race: number; meeting: number }) {
    const selectionRace = this.meetings().flatMap(rn => rn.races).find(r => r.id == selection.race && r.meetingId === selection.meeting);
    const selectionMeeting = this.meetings().find(rn => rn.id == selection.meeting)

    this.pmuService.updateData({
      meeting: selectionMeeting,
      race: selectionRace,
      chevalBases: [],
      nCombine: 0,
      montant: 0,
      payable: 0,
      chevalAssocies: [],
      // game: '',
    });
    this.meetings()
      .flatMap(meeting => meeting.races)
      .flatMap(r => r.horses)
      .forEach((p) => (p.selectionne = false, p.associe = false));
  }

  reinitializeGame() {
    this.spotEnabled = false;
    this.selectedFormula = 'SI';
    this.completeFormulaEnabled = false;
  }

  getHorses() {
    let meeting = this.meetings().find((r) => r.id === this.pari().meeting?.id);

    if (meeting) {
      let race = meeting.races.find((c) => c.id === this.pari().race?.id);
      if (race) {
        return race.horses;
      }
    }

    return [];
  }

  updateGame(details: {
    game: Game;
    formula: GameFormula;
    spot: boolean;
    completeFormula: boolean;
    multi: number
  }) {
    if(this.betslipComponent){
      this.betslipComponent.deleteBet();
    }
    this.spotEnabled = details.spot;
    this.selectedFormula = details.formula;
    this.completeFormulaEnabled = details.completeFormula;
    this.selectedGame.set(details.game);
    this.pmuService.updateData({ game: details.game, chevalBases: [], chevalAssocies: [], completeFormula: details.completeFormula, mise: details.game.baseStake, nCombine: 0, multi: details.multi, sformula: details.formula });
    this.showAssociateSelect.set(["CP", "CT"].includes(details.formula));
    this.updateSelected({race: details.game.raceId, meeting: details.game.meetingId});
  }

  handleVoirDetails(open: boolean) {
    this.showListDetails.set(open);
    // if(!open) {
    //   this.selectedFormula = "SI";
    //   this.pmuService.updateData({...this.pmuService.getCurrentData(), sformula: this.selectedFormula});
    // }
  }

  handleSpotChange(details: { game: Game; formula: GameFormula; spot: boolean; completeFormula: boolean }) {
    this.isSpot = details.spot;
    this.pmuService.updateData({...this.pmuService.getCurrentData(), spotEnabled: details.spot, completeFormula: details.completeFormula, sformula: details.formula});
  }

  updateGameAmount(details: {
      mise: number;
      coefficient: number;
    }) {
      // Update mise of the bet (stake * coefficient)
      this.pmuService.updateData({
        mise: details.mise,
        coefficient: details.coefficient,
        montant: details.mise * this.pmuService.getCurrentData().nCombine,
        payable: details.mise * this.pmuService.getCurrentData().nCombine,
      });
    }

  updateSelectedHorses(selections: {base: string[], associes: string[]}) {
    // const selections = selection.base.concat(selection.associes)
    if(["CT"].includes(this.selectedFormula)) {
      const allHorses = this.getHorses().map(h => h.numero);
      const associates = allHorses.filter(num => !selections.base.includes(num));
      selections.associes = associates;
    }
    const nCombine = this.getNumberCombinations(selections);

    this.pmuService.updateData({
      chevalBases: selections.base,
      nCombine,
      montant: this.pmuService.getCurrentData().mise * nCombine,
      payable: this.pmuService.getCurrentData().mise * nCombine,
      chevalAssocies: selections.associes
    });
  }
  getNumberCombinations(selections: {base: string[], associes: string[]}) {
    let [nCombine, nBase, nAssocies, nHorses] = [0, selections.base.length, selections.associes.length, selections.base.length + selections.associes.length];
    let [gameBase, gameCode, completFormula] = [this.selectedGame().baseHorseCount ?? 0, this.selectedGame().code, this.pari().completeFormula];
    switch (this.selectedFormula) {
      case 'SI':  // Simple
        nCombine = SI(nBase, gameBase, completFormula);
        break;
      case 'CE':  // Champs Elargie
        nCombine = CE(nBase, gameBase, completFormula);
        break;
      case 'CT':  // Champs Total
        nCombine = CT(nBase, gameBase, nAssocies, this.pari().race?.competitorsCount ?? 0, !!this.selectedGame().hasOrder, completFormula);
        break;
      case 'CP':  // Champs Partielle
        nCombine = CP(nBase, gameBase, nAssocies, completFormula, this.selectedGame().hasOrder);
        break;
    }
    return nCombine
  }

  /**
   * This method is called when the user clicks on the "Clear Selections" button
   * in the BetslipComponent.
   * It clears the selections in the HorseListComponent
  */
  handleClearSelections() {
    if (this.horseListComponent) {
      this.horseListComponent.clearAllSelections();
    }
  }

  toggleFormuleComplet($event: boolean) {
    let selections = {
      base: this.pmuService.getCurrentData().chevalBases,
      associes: this.pmuService.getCurrentData().chevalAssocies
    }
    this.updateSelectedHorses(selections);
  }
}
